import { Alert, DatePicker, Form, Input, InputNumber, Modal, Radio, Select, Space, TimePicker, Typography } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { formatDate, useActiveTeachers } from "../app/classes.ts";
import { cancellationCauses, lessonKeys, useShowLessonWarnings, type CancellationCause, type LessonDetails } from "../app/lessons.ts";
import { maxPeriod } from "../app/timetable.ts";

export type LessonAction = "cancel" | "move" | "substitute";

interface Props {
  classId: string;
  lesson: LessonDetails | undefined;
  action: LessonAction;
  yearStart: string;
  yearEnd: string;
  onClose(): void;
}

/** Field names match the API (cause, reason, date, period, startTime, endTime, teacherId) so server errors land on the inputs. */
interface ActionForm {
  cause?: CancellationCause;
  reason?: string;
  date?: Dayjs;
  period?: number;
  startTime?: Dayjs;
  endTime?: Dayjs;
  teacherId?: string;
}

const REASON_MAX_LENGTH = 500;

function timeValue(time: string): Dayjs {
  const [h = "0", m = "0"] = time.split(":");
  return dayjs().hour(Number(h)).minute(Number(m)).second(0);
}

function apiTime(value: Dayjs | undefined): string | null {
  return value ? value.format("HH:mm:00") : null;
}

/**
 * One dialog for the three lesson changes: cancel (who caused it and why), move (new date, period and times; the server rejects
 * non-school days and clashes) and substitute (any active teacher other than the assigned one).
 */
export function LessonActionModal({ classId, lesson, action, yearStart, yearEnd, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ActionForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const teachers = useActiveTeachers(lesson !== undefined && action === "substitute");
  const showWarnings = useShowLessonWarnings();

  // Each opening starts from the lesson's current values.
  useEffect(() => {
    if (!lesson) return;
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(lesson.date),
      period: Number(lesson.period),
      startTime: timeValue(lesson.startTime),
      endTime: timeValue(lesson.endTime),
      ...(lesson.isSubstituted && lesson.teacherId ? { teacherId: lesson.teacherId } : {}),
    });
    setSummary([]);
  }, [lesson, action, form]);

  const mutation = useMutation({
    mutationFn: async (values: ActionForm) => {
      if (!lesson) return;
      const path = { id: lesson.id };
      const result =
        action === "cancel"
          ? await api.POST("/academic/lessons/{id}/cancel", { params: { path }, body: { cause: values.cause ?? null, reason: values.reason ?? "" } })
          : action === "move"
            ? await api.POST("/academic/lessons/{id}/move", {
                params: { path },
                body: { date: values.date?.format("YYYY-MM-DD") ?? null, period: values.period ?? 0, startTime: apiTime(values.startTime), endTime: apiTime(values.endTime) },
              })
            : await api.PUT("/academic/lessons/{id}/substitute", { params: { path }, body: { teacherId: values.teacherId ?? null } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: lessonKeys.all(classId) });
      setSummary([]);
      onClose();
      showWarnings(saved?.warnings);
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const teacherOptions = (teachers.data ?? [])
    .filter((teacher) => teacher.id !== lesson?.assignedTeacherId)
    .map((teacher) => ({ value: teacher.id, label: teacher.fullName }));
  const disabledDate = (d: Dayjs) => d.isBefore(dayjs(yearStart), "day") || d.isAfter(dayjs(yearEnd), "day");
  const required = { required: true, message: t("family.validation.required") };

  return (
    <Modal
      open={lesson !== undefined}
      title={t(`lessons.${action}Title`)}
      okText={t("family.save")}
      cancelText={t("family.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {lesson && (
        <Typography.Paragraph type="secondary">
          {t("lessons.lessonLabel", { subject: lesson.subjectName, date: formatDate(lesson.date), period: Number(lesson.period) })}
        </Typography.Paragraph>
      )}
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      <Form<ActionForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
        {action === "cancel" && (
          <>
            <Form.Item name="cause" label={t("lessons.cause")} rules={[required]}>
              <Radio.Group options={cancellationCauses.map((c) => ({ value: c, label: t(`lessons.causes.${c}`) }))} />
            </Form.Item>
            <Form.Item name="reason" label={t("lessons.reason")} rules={[{ ...required, whitespace: true }, { max: REASON_MAX_LENGTH }]}>
              <Input.TextArea rows={3} maxLength={REASON_MAX_LENGTH} showCount />
            </Form.Item>
          </>
        )}
        {action === "move" && (
          <>
            <Form.Item name="date" label={t("lessons.newDate")} rules={[required]}>
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} disabledDate={disabledDate} />
            </Form.Item>
            <Form.Item name="period" label={t("timetable.period")} rules={[required]}>
              <InputNumber min={1} max={maxPeriod} precision={0} style={{ width: "100%" }} />
            </Form.Item>
            <Space wrap>
              <Form.Item name="startTime" label={t("timetable.startTime")} rules={[required]}>
                <TimePicker format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} />
              </Form.Item>
              <Form.Item name="endTime" label={t("timetable.endTime")} rules={[required]}>
                <TimePicker format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} />
              </Form.Item>
            </Space>
          </>
        )}
        {action === "substitute" && (
          <Form.Item name="teacherId" label={t("lessons.substitute")} extra={t("lessons.substituteHint")} rules={[required]}>
            <Select options={teacherOptions} loading={teachers.isPending} showSearch={{ optionFilterProp: "label" }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
